// Owner: shell-nav (plan §9.2, interaction-systems F18, shell-ia F-24). Designed error and not-found
// states for every route group: the 404 sits inside the shell for signed-in members (with search and the
// way home), errors show what happened with Try again, and the document title follows the route.
import { useQuery } from '@tanstack/react-query';
import { Link, type ErrorComponentProps, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, ErrorState, NotFound, Spinner, buttonClass } from '@dataroom/ui';
import type * as SettingsNav from '../features/account/settings-nav';
import { useRememberedBrand } from '../features/branding/favicon';
import { t } from '../lib/i18n';
import { meQuery, navQuery, recentQuery } from '../lib/queries';
import { AppShell } from './AppShell';
import { openLayer } from './overlays';
import { Topbar } from './Topbar';
import { composeTitle, routeTitle, useDocumentTitle, useTitleOverride } from './title';

/** The not-found page body (inside the shell for members, alone for visitors). */
export function NotFoundPage({
  signedIn,
  title = t('common.notFound'),
  description = t('shell.notFound.body'),
  query,
}: {
  signedIn: boolean;
  /** A thing that is missing inside a route that exists (an object): its own wording, same actions. */
  title?: string;
  description?: string;
  /** What "Search" starts with; the last segment of the path by default. */
  query?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const guess =
    query ?? decodeURIComponent(pathname.split('/').filter(Boolean).at(-1) ?? '').replace(/[-_]+/g, ' ');
  useDocumentTitle({ entity: title, state: 'error' });
  return (
    <>
      {/* Inside the shell the page keeps its top bar (breadcrumb, bell), like every other page. */}
      {signedIn ? <Topbar crumbs={[{ label: title }]} /> : null}
      <main className="shell-state" data-testid="not-found">
        {/* The page's heading for assistive tech; the visible title is the state's own heading. */}
        <h1 className="sr-only">{t('shell.notFound.heading')}</h1>
        <NotFound
          title={title}
          description={description}
          action={
            signedIn ? (
              <>
                <Button
                  variant="primary"
                  icon="search"
                  onClick={() => openLayer('palette', { query: guess })}
                  data-testid="not-found-search"
                >
                  {t('shell.notFound.search')}
                </Button>
                <Link to="/" className={buttonClass({ variant: 'secondary' })}>
                  {t('shell.notFound.home')}
                </Link>
              </>
            ) : (
              <Link to="/login" className={buttonClass({ variant: 'primary' })}>
                {t('login.title')}
              </Link>
            )
          }
        />
      </main>
    </>
  );
}

/** The root's not-found: inside the app shell when the visitor is signed in. */
export function RootNotFound() {
  const me = useQuery({ ...meQuery, retry: false });
  if (me.isPending) return null;
  if (me.data)
    return (
      <AppShell>
        <NotFoundPage signedIn />
      </AppShell>
    );
  return (
    <div className="shell-state-page">
      <NotFoundPage signedIn={false} />
    </div>
  );
}

/** Not-found inside a layout that already renders the shell (`_app`). */
export function ShellNotFound() {
  return <NotFoundPage signedIn />;
}

/** A route's error: plain words, Try again (reloads the route), details to copy. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useDocumentTitle({ entity: t('shell.error.title'), state: 'error' });
  return (
    <main className="shell-state" data-testid="route-error">
      <h1 className="sr-only">{t('shell.error.title')}</h1>
      <ErrorState
        error={error}
        title={t('shell.error.title')}
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        actions={
          <Link to="/" className={buttonClass({ variant: 'secondary', size: 'sm' })}>
            {t('shell.notFound.home')}
          </Link>
        }
      />
    </main>
  );
}

/**
 * A layout still loading (session, language, first data) after the router's pending delay: a calm
 * spinner instead of a blank page, and words once it takes longer than usual (never a bare spinner
 * beyond 10 s).
 */
export function PagePending() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setSlow(true), 9_000);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div className="shell-state-page shell-pending" data-testid="page-pending" aria-busy="true">
      <Spinner size={22} />
      {slow ? (
        <p className="shell-pending-text" role="status">
          {t('shell.slow.body')}
        </p>
      ) : null}
    </div>
  );
}

/** A route's error outside the shell (public pages, the root). */
export function PageError(props: ErrorComponentProps) {
  // Outside the app frame: wear the brand of the workspace this browser last worked in.
  useRememberedBrand();
  return (
    <div className="shell-state-page">
      <RouteError {...props} />
    </div>
  );
}

/** Sets `document.title` for every route: `<Entity> · <Section> — <Workspace>`. Mounted once in the root. */
type SettingsNavModule = typeof SettingsNav;
let settingsNavModule: SettingsNavModule | null = null;

/**
 * account-settings' settings model, loaded once a settings or Account page is shown (its layout has
 * loaded it already, so this resolves at once); never part of the first paint elsewhere.
 */
function useSettingsNav(pathname: string): SettingsNavModule | null {
  const wanted = /^\/(settings|account)(\/|$)/.test(pathname);
  const [mod, setMod] = useState(settingsNavModule);
  useEffect(() => {
    if (!wanted || mod) return;
    let live = true;
    void import('../features/account/settings-nav').then((m) => {
      settingsNavModule = m;
      if (live) setMod(m);
    });
    return () => {
      live = false;
    };
  }, [wanted, mod]);
  return mod;
}

export function TitleHost() {
  const location = useRouterState({ select: (s) => s.location });
  // Read what is cached; never fetch for the title (public pages stay anonymous).
  const me = useQuery({ ...meQuery, enabled: false }).data;
  const nav = useQuery({ ...navQuery, enabled: false }).data;
  const recent = useQuery({ ...recentQuery(8), enabled: false }).data;
  const override = useTitleOverride();
  const settingsNav = useSettingsNav(location.pathname);
  const derived = routeTitle(location.pathname, location.search, {
    nav,
    recent: recent?.items,
    settingsPage: settingsNav
      ? (p) => {
          const entry = settingsNav.activeSettingsEntry(p);
          return entry ? { label: t(entry.label), account: entry.group === 'account' } : null;
        }
      : undefined,
  });
  const title = composeTitle(override ?? derived, me?.workspace.name);
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
}
